import { apiClient } from './api'

export type PartnerSettings = {
  id: string
  partner_organization_id: string
  provider_organization_id: string
  commission_rate: number
  payment_terms: string
  is_active: boolean
  notes?: string
  created_at: string
  updated_at: string
}

export type PartnerSettingsUpdate = {
  commission_rate?: number
  payment_terms?: string
  is_active?: boolean
  notes?: string
}

export type PartnerPerformance = {
  id: string
  partner_organization_id: string
  metric_date: string
  total_sales: number
  total_orders: number
  commission_earned: number
  customer_satisfaction: number
  new_customers: number
  active_customers: number
}

export type PartnerDashboard = {
  total_partners: number
  active_partners: number
  pending_applications: number
  total_sales: number
  total_commission: number
  average_satisfaction: number
  unread_notifications: number
  recent_applications: {
    id: string
    company_name: string
    status: string
    created_at: string
    business_model: string
  }[]
}

export type PartnerMetricsSummary = {
  partner_id?: string
  period_days: number
  total_sales: number
  total_orders: number
  total_commission: number
  average_satisfaction: number
  sales_trend: number
  orders_trend: number
}

export const partnersApi = {
  // Get all partner settings
  async getSettings(activeOnly: boolean = true): Promise<PartnerSettings[]> {
    const params = new URLSearchParams()
    params.append('active_only', activeOnly.toString())
    return apiClient.get<PartnerSettings[]>(`/api/v1/partners/settings?${params}`)
  },

  // Get specific partner settings
  async getPartnerSettings(partnerId: string): Promise<PartnerSettings> {
    return apiClient.get<PartnerSettings>(`/api/v1/partners/settings/${partnerId}`)
  },

  // Update partner settings
  async updatePartnerSettings(
    partnerId: string,
    data: PartnerSettingsUpdate,
  ): Promise<PartnerSettings> {
    return apiClient.patch<PartnerSettings>(`/api/v1/partners/settings/${partnerId}`, data)
  },

  // Get partner performance metrics
  async getPerformance(
    partnerId?: string,
    startDate?: string,
    endDate?: string,
  ): Promise<PartnerPerformance[]> {
    const params = new URLSearchParams()
    if (partnerId) params.append('partner_id', partnerId)
    if (startDate) params.append('start_date', startDate)
    if (endDate) params.append('end_date', endDate)
    return apiClient.get<PartnerPerformance[]>(`/api/v1/partners/performance?${params}`)
  },

  // Get partner dashboard
  async getDashboard(): Promise<PartnerDashboard> {
    return apiClient.get<PartnerDashboard>('/api/v1/partners/dashboard')
  },

  // Get metrics summary
  async getMetricsSummary(partnerId?: string, days: number = 30): Promise<PartnerMetricsSummary> {
    const params = new URLSearchParams()
    if (partnerId) params.append('partner_id', partnerId)
    params.append('days', days.toString())
    return apiClient.get<PartnerMetricsSummary>(`/api/v1/partners/metrics/summary?${params}`)
  },

  // Get top performing partners
  async getTopPerformers(limit: number = 10, days: number = 30): Promise<{ partners: any[] }> {
    const params = new URLSearchParams()
    params.append('limit', limit.toString())
    params.append('days', days.toString())
    return apiClient.get<{ partners: any[] }>(`/api/v1/partners/metrics/top-performers?${params}`)
  },

  // Create partner settings (when none exist)
  async createPartnerSettings(
    partnerId: string,
    data: Partial<PartnerSettings>,
  ): Promise<PartnerSettings> {
    return apiClient.post<PartnerSettings>('/api/v1/partners/settings', {
      partner_organization_id: partnerId,
      ...data,
    })
  },
}
